import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { businessUnitsApi } from '@/lib/api';
import { unitErrorMessage, unitLabel, useVisibleUnits } from '@/lib/business-units';
import { queryKeys } from '@/lib/query-keys';
import { UnitRoleSelect } from './unit-role-select';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { id: string; name: string };
  /** Their primary employer today. */
  currentUnitId: string;
}

/**
 * Move an employee to another primary employer — the unit that holds their HR
 * record and their cost — with a role there. They can keep access to the unit
 * they leave, with a role of it; if not, they lose it (refused while they are
 * still on one of its customers' teams). A Super Admin's.
 */
export function ChangePrimaryEmployerDialog({ open, onOpenChange, user, currentUnitId }: Props) {
  const businessUnits = useVisibleUnits();
  const queryClient = useQueryClient();
  const [unitId, setUnitId] = useState<string | null>(null);
  const [roleId, setRoleId] = useState<string | null>(null);
  const [keep, setKeep] = useState(false);
  const [keepRoleId, setKeepRoleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUnitId(null);
    setRoleId(null);
    setKeep(false);
    setKeepRoleId(null);
    setError(null);
  }, [open]);

  const current = businessUnits.find((u) => u.id === currentUnitId);
  const move = useMutation({
    mutationFn: () =>
      businessUnitsApi.changePrimaryEmployer(unitId as string, {
        userId: user.id,
        systemRoleId: roleId as string,
        keepPreviousWithRoleId: keep ? keepRoleId : null,
      }),
    onSuccess: () => {
      toast.success('Primary employer changed');
      queryClient.invalidateQueries({ queryKey: queryKeys.businessUnits.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(user.id) });
      onOpenChange(false);
    },
    onError: (err) => setError(unitErrorMessage(err, 'Could not change the primary employer.')),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change primary employer</DialogTitle>
          <DialogDescription>
            {user.name}'s HR record, manager and cost move to the new unit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>New primary employer</Label>
            <Select
              value={unitId ?? undefined}
              onValueChange={(v) => {
                setUnitId(v);
                setRoleId(null);
              }}
            >
              <SelectTrigger className="w-full" aria-label="New primary employer">
                <SelectValue placeholder="Choose a Business Unit" />
              </SelectTrigger>
              <SelectContent>
                {businessUnits
                  .filter((u) => u.id !== currentUnitId)
                  .map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {unitLabel(u)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Role there</Label>
            <UnitRoleSelect businessUnitId={unitId} value={roleId} onChange={setRoleId} />
          </div>
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="keep-previous"
                checked={keep}
                onCheckedChange={(v) => setKeep(v === true)}
              />
              <Label htmlFor="keep-previous" className="font-normal">
                Keep access to {current ? unitLabel(current) : 'their current unit'}
              </Label>
            </div>
            {keep ? (
              <UnitRoleSelect
                businessUnitId={currentUnitId}
                value={keepRoleId}
                onChange={setKeepRoleId}
                aria-label="Role in the unit they leave"
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                Without it they lose access there — refused while they are still on the team of one
                of its customers.
              </p>
            )}
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => move.mutate()}
            disabled={!unitId || !roleId || (keep && !keepRoleId) || move.isPending}
          >
            {move.isPending ? 'Saving…' : 'Change primary employer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
